/**
 * Sparkline — dependency-free inline-SVG line chart for the Mango — Farmer
 * Market kit. No charting library: just a single <polyline> over a normalised
 * viewBox, so the trend renders crisply at any size with zero runtime cost.
 *
 * Theme-aware: defaults to the MD3 `primary` role (resolves light/dark) but any
 * color token can be passed (e.g. the kit's error role for a downward trend).
 *
 *   <Sparkline points={[1,2,1.5,3,2.8]} />
 *   <Sparkline points={series} color={sys.error} width={140} height={36} />
 *
 * Degenerate inputs (0 or 1 point, or a flat series) draw a centred baseline so
 * the slot never collapses. Purely presentational — no axes, labels, or ticks.
 */
import { Box } from '@mui/material';
import { forwardRef, useId } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor } from '../theme/tokens.js';

export interface SparklineProps {
  /** Y-values in series order (oldest → newest). 0/1 points draw a baseline. */
  points: number[];
  /** Render width in px. Default 120. */
  width?: number;
  /** Render height in px. Default 32. */
  height?: number;
  /** Line colour. Default the theme `primary` role (mode-aware). */
  color?: string;
  /** Stroke width in px. Default 2. */
  strokeWidth?: number;
  /** Accessible label; defaults to a generic description. */
  ariaLabel?: string;
  /** Pass-through className for layout composition. */
  className?: string;
}

export const Sparkline = forwardRef<HTMLDivElement, SparklineProps>(function Sparkline(
  { points, width = 120, height = 32, color, strokeWidth = 2, ariaLabel, className },
  ref,
) {
  const { mode } = useColorMode();
  const stroke = color ?? mdSysColor[mode].primary;
  const gradientId = useId();

  // Inset by the stroke radius so the line never clips at the edges.
  const pad = strokeWidth;
  const innerW = Math.max(0, width - pad * 2);
  const innerH = Math.max(0, height - pad * 2);

  const min = points.length ? Math.min(...points) : 0;
  const max = points.length ? Math.max(...points) : 0;
  const span = max - min;

  // Map each point into the inner box. With <2 points or a flat series, pin the
  // line to the vertical centre so we still draw a visible baseline.
  const coords = points.map((value, i) => {
    const x = points.length <= 1 ? innerW / 2 : (i / (points.length - 1)) * innerW;
    const y = span === 0 ? innerH / 2 : innerH - ((value - min) / span) * innerH;
    return { x: pad + x, y: pad + y };
  });

  // A single point can't form a polyline; widen it to a flat 2-point baseline.
  const drawn =
    coords.length === 1 ? [{ ...coords[0]!, x: pad }, { ...coords[0]!, x: pad + innerW }] : coords;
  const pointsAttr = drawn.map((c) => `${c.x.toFixed(2)},${c.y.toFixed(2)}`).join(' ');

  // Soft fill under the line (line → bottom → back), for the "tape" feel.
  const areaAttr =
    drawn.length >= 2
      ? `${pointsAttr} ${(pad + innerW).toFixed(2)},${(pad + innerH).toFixed(2)} ${pad.toFixed(2)},${(pad + innerH).toFixed(2)}`
      : '';

  return (
    <Box
      ref={ref}
      className={className}
      sx={{ display: 'inline-flex', lineHeight: 0 }}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel ?? 'Price trend sparkline'}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity={0.18} />
            <stop offset="100%" stopColor={stroke} stopOpacity={0} />
          </linearGradient>
        </defs>
        {areaAttr ? <polygon points={areaAttr} fill={`url(#${gradientId})`} stroke="none" /> : null}
        <polyline
          points={pointsAttr}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
});
